import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Session } from '@supabase/supabase-js'
import type { Profile } from '@/models'
import { isHost } from '@/models'
import { supabase } from '@/lib/supabase'
import { getProfile } from '@/services/authService'

interface AuthState {
  user: User | null
  session: Session | null
  profile: Profile | null
  isLoading: boolean
  isInitialized: boolean
  unreadMessageCount: number

  // Actions
  initialize: () => Promise<void>
  setUser: (user: User | null) => void
  setSession: (session: Session | null) => void
  setProfile: (profile: Profile | null) => void
  loadProfile: (userId: string) => Promise<void>
  clearAuth: () => void
  setUnreadCount: (count: number) => void
  incrementUnread: () => void
  resetUnread: () => void
}

// Module-level guards. React StrictMode runs the initialising effect twice in
// dev; these ensure auth starts once and only one onAuthStateChange listener is
// ever registered (no leaked/duplicate subscriptions).
let authInitStarted = false
let authListenerUnsub: (() => void) | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Load the profile OUTSIDE the onAuthStateChange callback.
      //
      // IMPORTANT: Supabase holds a non-reentrant auth lock for the duration of
      // the onAuthStateChange callback. Awaiting any Supabase call (such as the
      // profiles query inside getProfile) from within that callback deadlocks
      // the lock — every subsequent query then hangs "pending" until a full page
      // refresh rebuilds the client. That deadlock was the cause of the
      // "loading spinner on returning to an idle tab" bug. Profile loading
      // therefore always runs here, called either at top level (initialize) or
      // via setTimeout(…, 0) from the listener — i.e. only after the callback
      // has returned and released the lock.
      const hydrateProfile = async (userId: string) => {
        try {
          const profile = await getProfile(userId)
          set({ profile, isLoading: false })
          applyTheme(profile)
        } catch (e) {
          console.error('[authStore] Failed to load profile:', e)
          // Never leave the app stuck on a spinner if the profile fetch fails.
          set({ isLoading: false })
        }
      }

      return {
        user: null,
        session: null,
        profile: null,
        isLoading: true,
        isInitialized: false,
        unreadMessageCount: 0,

        initialize: async () => {
          // Run once per page load, even if the effect fires twice (StrictMode).
          if (authInitStarted) return
          authInitStarted = true

          set({ isLoading: true })

          // Get initial session
          const { data: { session } } = await supabase.auth.getSession()

          if (session?.user) {
            // Publish the session synchronously so the app can render, then
            // hydrate the profile (which sets isLoading:false + theme when done).
            set({ user: session.user, session, isInitialized: true })
            await hydrateProfile(session.user.id)
          } else {
            set({ isLoading: false, isInitialized: true })
          }

          // Register the auth listener exactly once.
          if (!authListenerUnsub) {
            const { data: { subscription } } = supabase.auth.onAuthStateChange(
              (event, session) => {
                // Keep this callback SYNCHRONOUS — never await a Supabase call
                // here (see hydrateProfile note above re: the auth-lock deadlock).
                if (event === 'SIGNED_IN' && session?.user) {
                  const userId = session.user.id
                  set({ user: session.user, session })
                  // Defer the profile fetch until after this callback returns
                  // and the auth lock has been released.
                  setTimeout(() => {
                    void hydrateProfile(userId)
                  }, 0)
                } else if (event === 'SIGNED_OUT') {
                  set({
                    user: null,
                    session: null,
                    profile: null,
                    isLoading: false,
                    unreadMessageCount: 0,
                  })
                  applyTheme(null)
                } else if (event === 'TOKEN_REFRESHED' && session) {
                  set({ session })
                } else if (event === 'USER_UPDATED' && session?.user) {
                  set({ user: session.user, session })
                }
              }
            )
            authListenerUnsub = () => subscription.unsubscribe()
          }
        },

        setUser: (user) => set({ user }),
        setSession: (session) => set({ session }),
        setProfile: (profile) => {
          set({ profile })
          applyTheme(profile)
        },

        loadProfile: async (userId: string) => {
          const profile = await getProfile(userId)
          set({ profile })
          applyTheme(profile)
        },

        clearAuth: () => {
          set({
            user: null,
            session: null,
            profile: null,
            unreadMessageCount: 0,
          })
          applyTheme(null)
        },

        setUnreadCount: (count) => set({ unreadMessageCount: count }),
        incrementUnread: () =>
          set((state) => ({ unreadMessageCount: state.unreadMessageCount + 1 })),
        resetUnread: () => set({ unreadMessageCount: 0 }),
      }
    },
    {
      name: 'tradie-auth',
      partialize: (state) => ({
        // Only persist non-sensitive data
        unreadMessageCount: state.unreadMessageCount,
      }),
    }
  )
)

// ── Helpers ────────────────────────────────────────────────────
function applyTheme(profile: Profile | null): void {
  const theme = isHost(profile) ? 'host' : 'renter'
  document.documentElement.setAttribute('data-theme', theme)
}

// ── Selectors ─────────────────────────────────────────────────
export const selectIsAuthenticated = (state: AuthState) =>
  state.user !== null && state.session !== null

export const selectIsHost = (state: AuthState) => isHost(state.profile)

export const selectUserDisplayName = (state: AuthState) =>
  state.profile?.full_name ||
  state.user?.email?.split('@')[0] ||
  'User'
